import React, { useState, useRef, useEffect, useContext } from "react";
import { ReactComponent as AvatarIcon } from "../Slike/accountIkona.svg";
import { ReactComponent as NotificationIcon } from "../Slike/notificationIcon.svg";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";

const Navbar = ({ setMenuOpen }) => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);

  const [userOpen, setUserOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const [tickets, setTickets] = useState([]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);

  const [notifications, setNotifications] = useState([]);

  const userRef = useRef(null);
  const notifRef = useRef(null);

  // ======================
  // NORMALIZER
  // ======================
  const normalize = (str = "") =>
    str
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  // ======================
  // ROLE FILTER (FIXED)
  // ======================
  const filterNotifications = (data, user) => {
    if (!user) return [];

    const role = (user.role || "").toLowerCase();

    if (role === "admin") {
      return data;
    }

    if (role === "ikt") {
      return data.filter((n) =>
        ["incident_created", "status_changed", "incident_resolved"].includes(
          n.type,
        ),
      );
    }

    if (role === "user") {
      return data.filter(
        (n) =>
          Number(n.user_id) === Number(user.id) &&
          ["incident_created", "incident_resolved"].includes(n.type),
      );
    }

    return [];
  };

  // ======================
  // LOAD INCIDENTS
  // ======================
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/getIncident.php`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success) setTickets(data.incidents || []);
      })
      .catch(console.error);
  }, []);

  // ======================
  // LOAD NOTIFICATIONS
  // ======================
  useEffect(() => {
    if (!user) return;

    fetch(`${process.env.REACT_APP_API_URL}/getNotification.php`)
      .then((res) => res.json())
      .then((data) => {
        const filtered = filterNotifications(data || [], user);
        setNotifications(filtered);
      })
      .catch(console.error);
  }, [user]);

  // ======================
  // CLOSE DROPDOWNS
  // ======================
  useEffect(() => {
    const handleClick = (e) => {
      if (!userRef.current?.contains(e.target)) setUserOpen(false);
      if (!notifRef.current?.contains(e.target)) setNotifOpen(false);
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // ======================
  // SEARCH
  // ======================
  useEffect(() => {
    const timeout = setTimeout(async () => {
      if (!search.trim()) {
        setResults([]);
        return;
      }

      const q = normalize(search);

      const incidentResults = tickets.filter((t) => {
        const idMatch = String(t.id).includes(q.replace(/[^0-9]/g, ""));
        const titleMatch = normalize(t.title).includes(q);
        const incMatch = `inc${t.id}`.toLowerCase().includes(q);

        return idMatch || titleMatch || incMatch;
      });

      let userResults = [];

      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/search.php?query=${encodeURIComponent(search)}`,
        );

        const data = await res.json();

        if (data.users?.length) {
          userResults = data.users.map((u) => ({
            ...u,
            type: "user",
          }));
        }
      } catch (err) {
        console.log(err);
      }

      setResults([
        ...userResults,
        ...incidentResults.map((i) => ({ ...i, type: "incident" })),
      ]);
    }, 200);

    return () => clearTimeout(timeout);
  }, [search, tickets]);

  // ======================
  // OPEN ITEM
  // ======================
  const openItem = (item) => {
    setSearch("");
    setResults([]);

    if (item.type === "user") {
      navigate(`/profile/${item.id}`);
    } else {
      navigate(`/incidents/${item.id}`);
    }
  };

  // ======================
  // NOTIFICATIONS CLICK
  // ======================
  const openNotifications = () => {
    setNotifOpen(!notifOpen);

    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
  };

  const unreadCount = notifications.filter((n) => n.is_read == 0).length;

  return (
    <div className="w-full bg-white p-2">
      <div className="bg-gray-50 flex items-center justify-between px-4 py-3 rounded-2xl shadow relative">
        {/* LEFT */}
        <div className="flex items-center gap-3 relative">
          <button
            onClick={() => setMenuOpen(true)}
            className="md:hidden text-2xl text-midBlue font-bold"
          >
            ☰
          </button>

          <input
            type="text"
            placeholder="Iskanje incident ali user..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="hidden md:block border border-gray-300 rounded-full px-4 py-2 text-sm w-72"
          />

          {results.length > 0 && (
            <div className="absolute top-12 left-0 bg-white shadow-xl rounded-xl w-72 z-50 max-h-72 overflow-y-auto">
              {results.map((r, i) => (
                <div
                  key={i}
                  onClick={() => openItem(r)}
                  className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                >
                  {r.type === "user" ? (
                    <>
                      <div className="font-semibold text-[#1A3D63]">USER</div>
                      <div className="text-sm text-gray-700">
                        {r.name} {r.surname}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="font-semibold text-[#1A3D63]">
                        INC{r.id}
                      </div>
                      <div className="text-sm text-gray-700">{r.title}</div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT */}
        <div className="flex items-center gap-2 relative">
          {/* NOTIFICATIONS */}
          <div className="relative" ref={notifRef}>
            <button
              onClick={openNotifications}
              className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 relative"
            >
              <NotificationIcon className="w-7 h-7 text-midBlue" />

              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                  {unreadCount}
                </span>
              )}
            </button>

            {notifOpen && (
              <div className="absolute right-0 top-12 w-80 bg-white rounded-2xl shadow-lg border z-50">
                <div className="p-3 border-b font-semibold text-[#1A3D63]">
                  Notifications
                </div>

                <div className="max-h-64 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="p-3 text-sm text-gray-500">Ni obvestil</div>
                  ) : (
                    notifications.slice(0, 10).map((n) => (
                      <div key={n.id} className="p-3 border-b text-sm">
                        <div className="font-semibold">{n.title}</div>
                        <div className="text-xs text-gray-500">{n.message}</div>
                        <div className="text-xs text-gray-400">
                          {n.created_at}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* USER */}
          <div className="relative" ref={userRef}>
            <button
              onClick={() => setUserOpen(!userOpen)}
              className="flex items-center gap-2 hover:bg-gray-100 px-2 py-2 rounded-xl"
            >
              <AvatarIcon className="w-8 h-8 text-midBlue" />

              {user && (
                <div className="hidden md:block text-left">
                  <p className="font-bold text-sm">
                    {user.name} {user.surname}
                  </p>
                  <p className="text-xs text-gray-400">{user.role}</p>
                </div>
              )}
            </button>

            {userOpen && (
              <div className="absolute right-0 top-14 w-44 bg-white rounded-2xl shadow-lg border z-50">
                <button
                  onClick={() => navigate(`/profile/${user.id}`)}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50"
                >
                  Račun
                </button>

                <button
                  onClick={() => {
                    logout();
                    navigate("/login");
                  }}
                  className="w-full text-left px-4 py-3 text-red-500 hover:bg-gray-50"
                >
                  Odjava
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navbar;
